import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "../components/ui/Button";
import { useCart } from "../context/CartContext";
import { supabase } from "../lib/supabaseClient";
import OrderStepper from "../components/OrderStepper";

const PROVINCES = [
  "AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"
].sort();

export default function Checkout() {
  useEffect(() => { document.title = "Checkout | HD Labs"; }, []);

  const navigate = useNavigate();
  const { items, totals, clearCart, province, setProvince, removeFromCart, setQty } = useCart();

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    phone: "",
    shipping_address1: "",
    shipping_address2: "",
    city: "",
    postal_code: "",
    country: "Canada",
    notes: "",
  });
  const [touched, setTouched] = useState({});

  // Prefill email if logged in
  useEffect(() => {
    let mounted = true;
    async function init() {
      const { data } = await supabase.auth.getSession();
      const session = data?.session || null;
      if (!mounted) return;
      const userEmail = session?.user?.email;
      if (userEmail) setForm(p => ({ ...p, customer_email: userEmail }));
    }
    init();
    return () => (mounted = false);
  }, []);

  const onChange = e => {
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));
    setTouched(p => ({ ...p, [e.target.name]: true }));
  };

  const canSubmit = useMemo(() => {
    if (!items.length) return false;
    const required = ["customer_name","customer_email","shipping_address1","city","postal_code","country","province"];
    return required.every(k => String(form[k] ?? province).trim().length > 0);
  }, [form, items.length, province]);

  const validateField = key => {
    const value = key === "province" ? province : form[key];
    return touched[key] && (!value || !String(value).trim());
  };

  const placeOrder = async e => {
    e.preventDefault();
    setTouched({
      customer_name: true,
      customer_email: true,
      shipping_address1: true,
      city: true,
      postal_code: true,
      country: true,
      province: true,
    });
    if (!canSubmit || loading) return;

    setLoading(true);
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id || null;
    const orderNumber = `HD-${Date.now()}`;

    const payload = {
      order_number: orderNumber,
      status: "AWAITING_PAYMENT",
      user_id: userId,
      name: form.customer_name.trim(),
      email: form.customer_email.trim().toLowerCase(),
      phone: form.phone.trim() || null,
      shipping_address1: form.shipping_address1.trim(),
      shipping_address2: form.shipping_address2.trim() || null,
      city: form.city.trim(),
      province,
      postal_code: form.postal_code.trim(),
      country: form.country.trim(),
      notes: form.notes.trim() || null,
      items: items.map(i => ({
        id: i.id,
        variantId: i.variant?.id || null,
        name: i.name,
        variantLabel: i.variant?.label || null,
        price: i.price,
        qty: i.qty,
        image: i.image,
        lineTotal: Number((i.price * i.qty).toFixed(2)),
      })),
      subtotal: Number(totals.subtotal.toFixed(2)),
      discount: Number(totals.discount.toFixed(2)),
      shipping: Number(totals.shipping.toFixed(2)),
      tax: Number(totals.tax.toFixed(2)),
      total: Number(totals.total.toFixed(2)),
    };

    const { data, error } = await supabase
      .from("orders")
      .insert([payload])
      .select("id")
      .single();

    setLoading(false);

    if (error) {
      alert("Order failed: " + error.message);
      return;
    }

    clearCart();
    navigate(`/order/${data.id}`);
  };

  if (!items.length) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <OrderStepper currentStep={1} />
          <div className="bg-white border rounded-3xl p-16 text-center shadow-sm mt-12">
            <div className="text-2xl font-semibold">Your cart is empty</div>
            <Link to="/shop" className="inline-block mt-8">
              <Button>Browse Shop</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <OrderStepper currentStep={1} />

        <div className="grid lg:grid-cols-3 gap-12 mt-12">

          {/* LEFT FORM */}
          <form onSubmit={placeOrder} className="lg:col-span-2 space-y-8">
            <div className="text-sm text-slate-700">
              Checking out as guest.{" "}
              <Link to="/login" className="text-blue-600 hover:underline">Sign in</Link>{" "}
              or{" "}
              <Link to="/signup" className="text-blue-600 hover:underline">create an account</Link>.
            </div>

            <Section title="Contact">
              <Field
                label="Full name"
                name="customer_name"
                value={form.customer_name}
                onChange={onChange}
                hasError={validateField("customer_name")}
              />
              <Field
                label="Email"
                name="customer_email"
                type="email"
                value={form.customer_email}
                onChange={onChange}
                hasError={validateField("customer_email")}
              />
              <Field
                label="Phone (optional)"
                name="phone"
                value={form.phone}
                onChange={onChange}
              />
            </Section>

            <Section title="Shipping">
              <Field
                label="Address line 1"
                name="shipping_address1"
                value={form.shipping_address1}
                onChange={onChange}
                hasError={validateField("shipping_address1")}
              />
              <Field
                label="Address line 2"
                name="shipping_address2"
                value={form.shipping_address2}
                onChange={onChange}
              />
              <Field
                label="City"
                name="city"
                value={form.city}
                onChange={onChange}
                hasError={validateField("city")}
              />
              <SelectField
                label="Province"
                value={province}
                onChange={e => setProvince(e.target.value)}
                options={PROVINCES}
                hasError={validateField("province")}
              />
              <Field
                label="Postal code"
                name="postal_code"
                value={form.postal_code}
                onChange={onChange}
                hasError={validateField("postal_code")}
              />
              <Field
                label="Country"
                name="country"
                value={form.country}
                onChange={onChange}
                hasError={validateField("country")}
              />
            </Section>
          </form>

          {/* RIGHT SUMMARY */}
          <div className="lg:col-span-1 mt-8 lg:mt-0">
            <div className="bg-white border rounded-3xl p-10 sticky top-24 shadow-lg flex flex-col justify-between h-full">

              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold">Order Summary</h2>
                  <span className="text-sm text-slate-500">{items.length} item{items.length !== 1 && "s"}</span>
                </div>

                <div className="border-t"></div>

                {/* Items */}
                <div className="space-y-4">
                  {items.map(i => (
                    <div key={i.id + i.variant?.id} className="flex justify-between items-center gap-4">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => removeFromCart(i.id, i.variant?.id)}
                          className="w-5 h-5 flex items-center justify-center text-slate-400 hover:text-slate-700 text-sm transition"
                          aria-label="Remove item"
                        >×</button>

                        {i.image && (
                          <img src={i.image} alt={i.name} className="w-14 h-14 object-cover rounded-lg border"/>
                        )}

                        <div className="space-y-1">
                          <div className="font-medium text-sm">
                            {i.name}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setQty(i.id, i.variant?.id, i.qty - 1)}
                              className="w-6 h-6 flex items-center justify-center border rounded text-xs"
                            >−</button>
                            <span className="text-sm w-5 text-center">{i.qty}</span>
                            <button
                              type="button"
                              onClick={() => setQty(i.id, i.variant?.id, i.qty + 1)}
                              className="w-6 h-6 flex items-center justify-center border rounded text-xs"
                            >+</button>
                          </div>
                        </div>
                      </div>
                      <div className="font-medium text-sm">${(i.price * i.qty).toFixed(2)}</div>
                    </div>
                  ))}
                </div>

                <div className="border-t"></div>

                {/* Totals */}
                <div className="space-y-2 text-sm">
                  <Row label="Subtotal" value={`$${totals.subtotal.toFixed(2)}`} />
                  {totals.discount > 0 && (
                    <Row
                      label="Discount"
                      value={<span className="text-green-600 font-semibold">- ${totals.discount.toFixed(2)}</span>}
                    />
                  )}
                  <Row
                    label="Shipping"
                    value={totals.shipping === 0
                      ? <span className="text-green-600 font-semibold">Free</span>
                      : `$${totals.shipping.toFixed(2)}`} />
                  <Row label="Tax" value={`$${totals.tax.toFixed(2)}`} />

                  <div className="border-t pt-3 mt-3">
                    <Row
                      label={<span className="font-semibold text-lg">Total</span>}
                      value={<span className="font-semibold text-lg">${totals.total.toFixed(2)}</span>}
                    />
                  </div>
                </div>
              </div>

              {/* Payment */}
              <div className="border-t pt-4 mt-4">
                <div className="text-sm text-slate-600 mb-3">
                  Interac e-Transfer. Payment instructions will shown after order is placed.
                </div>
                <Button
                  className="w-full py-4"
                  onClick={placeOrder}
                  disabled={!canSubmit || loading}
                >
                  {loading ? "Placing order..." : "Place order"}
                </Button>
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

/* --- Components --- */

function Section({ title, children }) {
  return (
    <div className="bg-white border rounded-3xl p-6 shadow-sm space-y-4">
      <div className="text-lg font-semibold">{title}</div>
      <div className="grid sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, hasError = false, className = "", ...props }) {
  return (
    <div className={className}>
      <label className="text-sm text-slate-700">{label}</label>
      <input
        {...props}
        className={`mt-2 w-full rounded-xl border px-4 py-3 text-sm focus:ring-1 focus:ring-blue-500 outline-none
          ${hasError ? "border-red-500" : "border-slate-200"}`}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options = [], hasError = false }) {
  return (
    <div>
      <label className="text-sm text-slate-700">{label}</label>
      <select
        value={value || ""}
        onChange={onChange}
        className={`mt-2 w-full rounded-xl border px-4 py-3 text-sm focus:ring-1 focus:ring-blue-500 outline-none
          ${hasError ? "border-red-500" : "border-slate-200"}`}
      >
        <option value="">Select...</option>
        {options.map(p => <option key={p} value={p}>{p}</option>)}
      </select>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <div className="text-slate-700">{label}</div>
      <div>{value}</div>
    </div>
  );
} 